import { useRef, PointerEvent } from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';

const CANVAS_W = 256;
const CANVAS_H = 490;

export type BlockType = 'text' | 'barcode';
export type TextAlign = 'left' | 'center' | 'right' | 'justify';

export interface CashdeskBlock {
  id: string;
  type: BlockType;
  content: string;
  anchor?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontWeight: 'normal' | 'bold';
  fontStyle: 'normal' | 'italic';
  textDecoration: 'none' | 'underline';
  textAlign: TextAlign;
  fontSize: number;
  rotation: number;
  zIndex: number;
}

function BarcodeSvg({ label, width, height }: { label: string; width: number; height: number }) {
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      <rect width={width} height={height} fill="#fff" stroke="#adb5bd" strokeWidth={1} />
      {Array.from({ length: 28 }, (_, i) => (
        <rect
          key={i}
          x={4 + i * 8}
          y={4}
          width={i % 3 === 0 ? 4 : 2}
          height={height - 18}
          fill={i % 4 === 0 ? '#adb5bd' : '#212529'}
          opacity={i % 4 === 0 ? 0.4 : 1}
        />
      ))}
      <text x={width / 2} y={height - 3} textAnchor="middle" fontSize={9} fill="#495057" fontFamily="monospace">
        {label}
      </text>
    </svg>
  );
}

interface CashdeskTicketPreviewProps {
  blocks: CashdeskBlock[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onUpdateBlock: (id: string, patch: Partial<CashdeskBlock>) => void;
  onRemoveBlock: (id: string) => void;
}

function CashdeskTicketPreview({ blocks, selectedId, onSelect, onUpdateBlock, onRemoveBlock }: CashdeskTicketPreviewProps) {
  const { sidebarColor } = useLayoutPreferences();
  const dragRef = useRef<{
    blockId: string;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);

  const onBlockPointerDown = (e: PointerEvent<HTMLDivElement>, blockId: string) => {
    e.stopPropagation();
    onSelect(blockId);
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;
    dragRef.current = {
      blockId,
      startX: e.clientX,
      startY: e.clientY,
      origX: block.x,
      origY: block.y,
    };
    (e.target as Element).setPointerCapture(e.pointerId);
  };

  const onCanvasPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const { blockId, startX, startY, origX, origY } = dragRef.current;
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    const newX = Math.max(0, Math.min(CANVAS_W - block.width, origX + dx));
    const newY = Math.max(0, Math.min(CANVAS_H - block.height, origY + dy));
    onUpdateBlock(blockId, { x: newX, y: newY });
  };

  const onCanvasPointerUp = () => {
    dragRef.current = null;
  };

  return (
    <Box>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75, fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>
        Aperçu du ticket
      </Typography>
      <Box
        sx={{
          width: CANVAS_W,
          height: CANVAS_H,
          border: '1.5px solid #495057',
          position: 'relative',
          backgroundColor: '#fff',
          overflow: 'hidden',
          cursor: 'default',
          userSelect: 'none',
          touchAction: 'none',
        }}
        onClick={() => onSelect(null)}
        onPointerMove={onCanvasPointerMove}
        onPointerUp={onCanvasPointerUp}
      >
        {blocks.map(block => (
          <Box
            key={block.id}
            sx={{
              position: 'absolute',
              left: block.x,
              top: block.y,
              width: block.width,
              height: block.height,
              transform: `rotate(${block.rotation}deg)`,
              zIndex: block.zIndex,
              border: selectedId === block.id ? `2px solid ${sidebarColor}` : '1px dashed #dee2e6',
              cursor: 'move',
              boxSizing: 'border-box',
              backgroundColor: '#fff',
            }}
            onPointerDown={e => onBlockPointerDown(e, block.id)}
            onClick={e => e.stopPropagation()}
          >
            {block.type === 'text' ? (
              <Box
                component="div"
                contentEditable
                suppressContentEditableWarning
                onInput={e => onUpdateBlock(block.id, { content: (e.target as HTMLElement).innerText })}
                sx={{
                  width: '100%',
                  height: '100%',
                  padding: '2px 4px',
                  boxSizing: 'border-box',
                  fontSize: block.fontSize,
                  fontWeight: block.fontWeight,
                  fontStyle: block.fontStyle,
                  textDecoration: block.textDecoration,
                  textAlign: block.textAlign,
                  outline: 'none',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflow: 'hidden',
                  lineHeight: 1.4,
                  color: '#212529',
                  cursor: 'text',
                }}
              >
                {block.content}
              </Box>
            ) : (
              <BarcodeSvg label={block.anchor ?? ''} width={block.width} height={block.height} />
            )}

            {selectedId === block.id && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -22,
                  left: 0,
                  display: 'flex',
                  gap: 0.25,
                  backgroundColor: '#fff',
                  border: `1px solid ${sidebarColor}40`,
                  borderRadius: 1,
                  px: 0.5,
                  py: 0.25,
                }}
                onPointerDown={e => e.stopPropagation()}
              >
                <Tooltip title="Rotation 90°">
                  <IconButton size="small" onClick={() => onUpdateBlock(block.id, { rotation: (block.rotation + 90) % 360 })} sx={{ p: 0.25 }}>
                    <RotateRightIcon sx={{ fontSize: '0.85rem', color: sidebarColor }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Avancer">
                  <IconButton size="small" onClick={() => onUpdateBlock(block.id, { zIndex: block.zIndex + 1 })} sx={{ p: 0.25 }}>
                    <ArrowUpwardIcon sx={{ fontSize: '0.85rem', color: '#495057' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reculer">
                  <IconButton size="small" onClick={() => onUpdateBlock(block.id, { zIndex: Math.max(0, block.zIndex - 1) })} sx={{ p: 0.25 }}>
                    <ArrowDownwardIcon sx={{ fontSize: '0.85rem', color: '#495057' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Supprimer">
                  <IconButton size="small" onClick={() => onRemoveBlock(block.id)} sx={{ p: 0.25 }}>
                    <DeleteOutlineIcon sx={{ fontSize: '0.85rem', color: '#dc3545' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default CashdeskTicketPreview;
